// Create/Edit Download Modal
// Modal for creating new downloads or editing existing ones
// Supports both create and edit modes with different header/button text

import React, { useState, useEffect, useRef } from 'react';
import { RevoltModal } from '../../../components/RevoltModal';
import { RevoltInput } from '../../../components/RevoltInput';
import { RevoltTextarea } from '../../../components/RevoltTextarea';
import { RevoltButton } from '../../../components/RevoltButton';
import DropdownSelect from '../../../components/DropdownSelect';
import './CreateEditDownloadModal.css';

const ACCESS_OPTIONS = [
  { value: 'anonymous', label: 'Anonymous' },
  { value: 'free', label: 'Free' },
  { value: 'members', label: 'Members Only' },
  { value: 'supporter', label: 'Supporter Only' },
  { value: 'admin', label: 'Admin Only' },
];

const LINK_TYPES = [
  { value: 'download', label: 'Download' },
  { value: 'navigation', label: 'Navigation' },
];

export function CreateEditDownloadModal({ isOpen, onClose, onSubmit, download = null }) {
  const isEdit = !!download;
  const coverFileInputRef = useRef(null);
  const galleryFileInputRef = useRef(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [accessType, setAccessType] = useState('anonymous');
  const [imageUrl, setImageUrl] = useState('');
  const [images, setImages] = useState([]);
  const [links, setLinks] = useState([]);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingGallery, setIsUploadingGallery] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Reset and populate form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (isEdit && download) {
        // Populate with existing download data
        setTitle(download.title || '');
        setDescription(download.description || '');
        setAccessType(download.accessType || 'anonymous');
        // Cover image
        setImageUrl(download.imageUrl || download.images?.find((i) => i.isCover)?.url || '');
        // Gallery images (exclude cover, preserve order)
        const galleryUrls = Array.isArray(download.images)
          ? download.images
              .filter((i) => !(i && typeof i === 'object' && i.isCover))
              .sort((a, b) => ((a?.order ?? 0) - (b?.order ?? 0)))
              .map((i) => (typeof i === 'string' ? i : i?.url))
              .filter(Boolean)
          : [];
        setImages(galleryUrls);
        // Links: normalize to { type, name, url, size }
        const normalizedLinks = Array.isArray(download.links)
          ? download.links.map((l) => ({
              type: l.type || 'download',
              name: l.name || '',
              url: l.url || '',
              size: l.size || '',
            }))
          : [];
        setLinks(normalizedLinks);
        setTags(Array.isArray(download.tags) ? download.tags : []);
        setCurrentGalleryIndex(0);
      } else {
        // Reset for create mode
        setTitle('');
        setDescription('');
        setAccessType('anonymous');
        setImageUrl('');
        setImages([]);
        setLinks([]);
        setTags([]);
        setCurrentGalleryIndex(0);
      }
      setNewTag('');
      setNewImageUrl('');
      setErrorMessage('');
      setIsUploadingCover(false);
      setIsUploadingGallery(false);
    }
  }, [isOpen, isEdit, download]);

  const uploadCoverImage = async (file) => {
    if (!file) return;

    setIsUploadingCover(true);
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch('/api/downloads/upload-image', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload cover image');
      }

      setImageUrl(data.imageUrl || '');
    } catch (error) {
      console.error('Error uploading cover image:', error);
      setErrorMessage(error.message || 'Failed to upload cover image');
    } finally {
      setIsUploadingCover(false);
      if (coverFileInputRef.current) {
        coverFileInputRef.current.value = '';
      }
    }
  };

  const uploadGalleryImages = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    setIsUploadingGallery(true);
    setErrorMessage('');

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('images', file);
      });

      const response = await fetch('/api/downloads/upload-images', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload gallery images');
      }

      const uploadedUrls = Array.isArray(data.images)
        ? data.images.map((image) => image?.url).filter(Boolean)
        : [];

      if (uploadedUrls.length > 0) {
        let firstNewIndex = 0;
        setImages((prev) => {
          const merged = [...prev];
          firstNewIndex = merged.length;
          uploadedUrls.forEach((url) => {
            if (!merged.includes(url)) {
              merged.push(url);
            }
          });
          return merged;
        });
        setCurrentGalleryIndex(firstNewIndex);
      }
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      setErrorMessage(error.message || 'Failed to upload gallery images');
    } finally {
      setIsUploadingGallery(false);
      if (galleryFileInputRef.current) {
        galleryFileInputRef.current.value = '';
      }
    }
  };

  const handleAddTag = () => {
    const tagValue = newTag.trim().toLowerCase();
    if (tagValue && !tags.includes(tagValue)) {
      setTags([...tags, tagValue]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleAddImage = () => {
    const url = newImageUrl.trim();
    if (url && !images.includes(url)) {
      const newImages = [...images, url];
      setImages(newImages);
      setCurrentGalleryIndex(newImages.length - 1); // Switch to the newly added image
      setNewImageUrl('');
    }
  };

  const handleRemoveImage = (imageToRemove) => {
    const newImages = images.filter((img) => img !== imageToRemove);
    setImages(newImages);

    // Adjust current index if necessary
    if (newImages.length === 0) {
      setCurrentGalleryIndex(0);
    } else if (currentGalleryIndex >= newImages.length) {
      setCurrentGalleryIndex(newImages.length - 1);
    }
  };

  const handlePrevGalleryImage = () => {
    setCurrentGalleryIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNextGalleryImage = () => {
    setCurrentGalleryIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleAddLink = () => {
    const newLink = { type: 'download', name: '', url: '', size: '' };
    setLinks([...links, newLink]);
  };

  const handleRemoveLink = (indexToRemove) => {
    setLinks(links.filter((_, index) => index !== indexToRemove));
  };

  const handleUpdateLink = (index, field, value) => {
    const updatedLinks = links.map((link, i) =>
      i === index ? { ...link, [field]: value } : link
    );
    setLinks(updatedLinks);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isUploadingCover || isUploadingGallery) {
      setErrorMessage('Please wait for uploads to finish before saving');
      return;
    }

    if (!title.trim()) {
      setErrorMessage('Title is required');
      return;
    }

    if (!description.trim()) {
      setErrorMessage('Description is required');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const normalizedImageUrls = [imageUrl.trim(), ...images.map((url) => url.trim())]
        .filter(Boolean)
        .filter((url, index, arr) => arr.indexOf(url) === index);

      const downloadData = {
        title: title.trim(),
        description: description.trim(),
        accessType,
        imageUrl: imageUrl.trim() || undefined,
        images: normalizedImageUrls,
        links: links.map(link => ({
          type: link.type,
          name: link.name.trim(),
          url: link.url.trim(),
          size: link.size.trim()
        })).filter(link => link.name && link.url),
        tags: [...tags],
      };

      const url = isEdit ? `/api/downloads/${download.id}` : '/api/downloads';
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(downloadData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${isEdit ? 'update' : 'create'} download`);
      }

      const result = await response.json();

      // Call the onSubmit callback if provided
      if (onSubmit) {
        await onSubmit(result.download || { id: download?.id, ...downloadData });
      }

      // Success - close modal
      onClose();
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} download:`, error);
      setErrorMessage(error.message || `Failed to ${isEdit ? 'update' : 'create'} download. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit Download" : "Create Download"}
      containerClassName="create-edit-download-modal-container modal-wide"
    >
      <div className="modal-scrollable-content">
        {errorMessage && (
          <div className="create-edit-download-modal-error">
            <i className="fa-solid fa-exclamation-triangle"></i>
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="create-edit-download-modal-form">
          <div className="create-edit-download-modal-columns">
            {/* Left Column - Title, Description, Tags */}
            <div className="create-edit-download-modal-left-column">
              <div className="form-group">
                <label htmlFor="download-title" className="form-label">
                  Title <span className="required">*</span>
                </label>
                <RevoltInput
                  id="download-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter download title..."
                  maxLength={200}
                />
                <div className="form-help">
                  A clear, descriptive name for your download.
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="download-description" className="form-label">
                  Description <span className="required">*</span>
                </label>
                <RevoltTextarea
                  id="download-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe this download..."
                  rows={6}
                />
                <div className="form-help">
                  A detailed description of what this download offers.
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Tags</label>

                {/* Current Tags */}
                {tags.length > 0 && (
                  <div className="tags-list">
                    {tags.map((tag, index) => (
                      <span key={index} className="tag-item">
                        {tag}
                        <button
                          type="button"
                          className="tag-remove"
                          onClick={() => handleRemoveTag(tag)}
                          aria-label={`Remove ${tag} tag`}
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Add New Tag */}
                <div className="add-tag-section">
                  <RevoltInput
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Add a tag..."
                    maxLength={30}
                  />
                  <RevoltButton
                    type="button"
                    variant="outline"
                    size="small"
                    onClick={handleAddTag}
                    disabled={!newTag.trim() || tags.includes(newTag.trim().toLowerCase())}
                  >
                    <i className="fa-solid fa-plus"></i>
                    Add
                  </RevoltButton>
                </div>
                <div className="form-help">
                  Tags help users find your download. Press Enter or click Add to add tags.
                </div>
              </div>
            </div>

            {/* Middle Column - Access Type, Links */}
            <div className="create-edit-download-modal-middle-column">
              <div className="form-group">
                <label htmlFor="download-access" className="form-label">
                  Access Type
                </label>
                <DropdownSelect
                  id="download-access"
                  value={accessType}
                  options={ACCESS_OPTIONS}
                  onValueChange={setAccessType}
                />
                <div className="form-help">
                  Anonymous: Anyone can download without login<br/>
                  Free: Requires login but no membership<br/>
                  Members: Requires active membership<br/>
                  Supporter: Requires supporter entitlement<br/>
                  Admin: Admin access only
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Links</label>

                {/* Current Links */}
                {links.length > 0 && (
                  <div className="links-list">
                    {links.map((link, index) => (
                      <div key={index} className="link-item">
                        <div className="link-item-content">
                          <DropdownSelect
                            value={link.type}
                            options={LINK_TYPES}
                            onValueChange={(value) => handleUpdateLink(index, 'type', value)}
                            className="link-type-select"
                          />
                          <RevoltInput
                            type="text"
                            value={link.name}
                            onChange={(e) => handleUpdateLink(index, 'name', e.target.value)}
                            placeholder="Display name"
                            className="link-name-input"
                          />
                          <RevoltInput
                            type="url"
                            value={link.url}
                            onChange={(e) => handleUpdateLink(index, 'url', e.target.value)}
                            placeholder="https://..."
                            className="link-url-input"
                          />
                          <RevoltInput
                            type="text"
                            value={link.size}
                            onChange={(e) => handleUpdateLink(index, 'size', e.target.value)}
                            placeholder="Size (e.g. 25 MB)"
                            className="link-size-input"
                          />
                          <button
                            type="button"
                            className="tag-remove link-remove"
                            onClick={() => handleRemoveLink(index)}
                            aria-label={`Remove link ${index + 1}`}
                          >
                            <i className="fa-solid fa-times"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Link */}
                <RevoltButton
                  type="button"
                  variant="outline"
                  size="small"
                  onClick={handleAddLink}
                  className="add-link-button"
                >
                  <i className="fa-solid fa-plus"></i>
                  Add Link
                </RevoltButton>
                <div className="form-help">
                  Add download links or navigation links. Downloads provide files, navigation links redirect users elsewhere.
                </div>
              </div>
            </div>

            {/* Right Column - Images */}
            <div className="create-edit-download-modal-right-column">
              {/* Thumbnail Image */}
              <div className="form-group">
                <label htmlFor="download-image" className="form-label">
                  Cover Image URL
                </label>
                <RevoltInput
                  id="download-image"
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                />
                <div className="form-help">
                  Main image displayed in download listings.
                </div>
                <div className="image-upload-actions">
                  <input
                    ref={coverFileInputRef}
                    type="file"
                    accept="image/*"
                    className="image-upload-input"
                    onChange={(e) => uploadCoverImage(e.target.files?.[0])}
                  />
                  <RevoltButton
                    type="button"
                    variant="outline"
                    size="small"
                    onClick={() => coverFileInputRef.current?.click()}
                    disabled={isUploadingCover || isUploadingGallery}
                  >
                    <i className={`fa-solid ${isUploadingCover ? 'fa-spinner fa-spin' : 'fa-upload'}`}></i>
                    {isUploadingCover ? 'Uploading Cover...' : 'Upload Cover Image'}
                  </RevoltButton>
                  <span className="image-upload-hint">Upload directly to Spaces or paste a URL above.</span>
                </div>
              </div>

              {/* Thumbnail Preview */}
              <div className="form-group">
                <label className="form-label">Cover Image Preview</label>
                <div className="image-preview-container">
                  {imageUrl ? (
                    <div className="image-preview-wrapper">
                      <img
                        src={imageUrl}
                        alt="Download cover preview"
                        className="image-preview"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300x300/333/666?text=Invalid+Image+URL';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="image-preview-placeholder">
                      <i className="fa-solid fa-image"></i>
                      <span>Enter a cover image URL above to see the preview</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Gallery Images */}
              <div className="form-group">
                <label className="form-label">Gallery Images</label>
                {images.length > 0 ? (
                  <div className="gallery-carousel">
                    {/* Main Gallery Image Display */}
                    <div className="image-preview-container image-preview-container--with-overlay">
                      <div className="image-preview-wrapper">
                        <img
                          src={images[currentGalleryIndex]}
                          alt={`Gallery ${currentGalleryIndex + 1}`}
                          className="image-preview"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/300x300/333/666?text=Invalid+Image+URL';
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        className="gallery-image-remove"
                        onClick={() => handleRemoveImage(images[currentGalleryIndex])}
                        aria-label={`Remove current gallery image`}
                      >
                        <i className="fa-solid fa-times"></i>
                      </button>

                      {/* Navigation Arrows - Only show if multiple images */}
                      {images.length > 1 && (
                        <>
                          <button
                            type="button"
                            className="gallery-nav gallery-nav--prev"
                            onClick={handlePrevGalleryImage}
                            aria-label="Previous gallery image"
                          >
                            <i className="fa-solid fa-chevron-left"></i>
                          </button>
                          <button
                            type="button"
                            className="gallery-nav gallery-nav--next"
                            onClick={handleNextGalleryImage}
                            aria-label="Next gallery image"
                          >
                            <i className="fa-solid fa-chevron-right"></i>
                          </button>
                        </>
                      )}
                    </div>

                    {/* Image Counter - Only show if multiple images */}
                    {images.length > 1 && (
                      <div className="gallery-counter">
                        {currentGalleryIndex + 1} / {images.length}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-gallery-images">
                    <p className="no-gallery-text">No gallery images yet</p>
                  </div>
                )}

                {/* Add New Gallery Image */}
                <div className="add-image-group">
                  <RevoltInput
                    type="url"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddImage())}
                    placeholder="https://example.com/gallery-image.jpg"
                  />
                  <RevoltButton
                    type="button"
                    variant="outline"
                    size="small"
                    onClick={handleAddImage}
                    disabled={!newImageUrl.trim() || images.includes(newImageUrl.trim())}
                  >
                    <i className="fa-solid fa-plus"></i>
                    Add
                  </RevoltButton>
                </div>
                <div className="image-upload-actions">
                  <input
                    ref={galleryFileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="image-upload-input"
                    onChange={(e) => uploadGalleryImages(e.target.files)}
                  />
                  <RevoltButton
                    type="button"
                    variant="outline"
                    size="small"
                    onClick={() => galleryFileInputRef.current?.click()}
                    disabled={isUploadingCover || isUploadingGallery}
                  >
                    <i className={`fa-solid ${isUploadingGallery ? 'fa-spinner fa-spin' : 'fa-images'}`}></i>
                    {isUploadingGallery ? 'Uploading Gallery...' : 'Upload Gallery Images'}
                  </RevoltButton>
                  <span className="image-upload-hint">Upload one or more images directly to Spaces.</span>
                </div>
                <div className="form-help">
                  Additional images shown in download details gallery.
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div className="create-edit-download-modal-footer">
        <div className="modal-actions">
          <RevoltButton
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </RevoltButton>

          <RevoltButton
            variant="primary"
            disabled={!title.trim() || !description.trim() || isSubmitting || isUploadingCover || isUploadingGallery}
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i> {isEdit ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEdit ? 'Update Download' : 'Create Download'
            )}
          </RevoltButton>
        </div>
      </div>
    </RevoltModal>
  );
}